import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { organizationSchema, createFAQSchema } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Check, X, Star, Zap, Type, Image, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";

const FluxVsIdeogram = () => {
  const faqs = [
    {
      question: "What is Ideogram best known for?",
      answer: "Ideogram is specifically designed for generating images with text. It excels at creating images with legible, accurate text rendering - ideal for posters, logos with text, memes, and social media graphics that require readable text."
    },
    {
      question: "Is FLUX better than Ideogram for product photos?",
      answer: "Yes, FLUX is superior for product photography. It offers better photorealism, more consistent quality across bulk generations, and is optimized for e-commerce use cases. Ideogram is better suited for graphics that require text."
    },
    {
      question: "Which is faster - FLUX or Ideogram?",
      answer: "FLUX is generally faster, especially for bulk generation where it can produce 1000+ images per hour. Ideogram is slower due to its text processing capabilities but produces excellent text-in-image results."
    },
    {
      question: "Can FLUX generate text in images?",
      answer: "FLUX can generate basic text but is not as reliable as Ideogram for complex text rendering. For projects requiring accurate text, Ideogram is the better choice. For pure imagery, FLUX excels."
    },
    {
      question: "Which one offers better value for money?",
      answer: "For bulk commercial use, FLUX offers better value with higher volume generation and consistent quality. Ideogram is valuable for specific text-heavy projects where accurate text rendering is critical."
    }
  ];

  const comparison = [
    { feature: "Text in Images", flux: "Basic", ideogram: "Excellent", winner: "ideogram" },
    { feature: "Photorealism", flux: "Excellent", ideogram: "Good", winner: "flux" },
    { feature: "Bulk Generation", flux: "1000+/hour", ideogram: "Limited", winner: "flux" },
    { feature: "Speed", flux: "Fast", ideogram: "Moderate", winner: "flux" },
    { feature: "E-commerce Use", flux: "Excellent", ideogram: "Good", winner: "flux" },
    { feature: "Logo Design", flux: "Good", ideogram: "Excellent", winner: "ideogram" },
    { feature: "Commercial Rights", flux: "Included", ideogram: "Included", winner: "tie" },
    { feature: "Free Tier", flux: "5/day", ideogram: "25/day", winner: "ideogram" },
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <SEO
        title="FLUX vs Ideogram 2026 | AI Image Generator Comparison for Text & Graphics"
        description="FLUX vs Ideogram comparison. Which AI is better for text-in-images, logos, and graphics? Complete analysis of features, quality, speed & pricing for 2026."
        keywords="flux vs ideogram, ideogram ai comparison, ai text in images, best ai for logos, flux ai vs ideogram ai, text generation ai, ai poster maker, ai logo with text"
        url="/flux-vs-ideogram"
        jsonLd={[
          organizationSchema,
          createFAQSchema(faqs)
        ]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'FLUX vs Ideogram', url: '/flux-vs-ideogram' }
        ]}
      />
      
      <Header />
      
      <main className="w-full">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 bg-gradient-to-br from-background via-background to-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-current" />
                Updated January 2026
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                FLUX vs Ideogram
                <span className="block text-primary mt-2">Which AI is Right for You?</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                FLUX excels at photorealistic images and bulk generation. Ideogram leads in text-in-image generation. 
                Find out which is best for your specific needs.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Verdict */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
              <div className="bg-card border rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold mb-2">Choose FLUX If:</h3>
                <ul className="text-left space-y-2 mt-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>You need bulk image generation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Product photography is your focus</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Speed and volume matter most</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>E-commerce is your use case</span>
                  </li>
                </ul>
                <Button asChild className="mt-6">
                  <Link to="/try-generator">Try FLUX Free</Link>
                </Button>
              </div>
              
              <div className="bg-card border rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold mb-2">Choose Ideogram If:</h3>
                <ul className="text-left space-y-2 mt-4">
                  <li className="flex items-center gap-2">
                    <Type className="w-5 h-5 text-blue-500" />
                    <span>Text in images is critical</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Type className="w-5 h-5 text-blue-500" />
                    <span>Creating posters or signage</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Type className="w-5 h-5 text-blue-500" />
                    <span>Logo design with text</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Type className="w-5 h-5 text-blue-500" />
                    <span>Social media graphics with text</span>
                  </li>
                </ul>
                <Button variant="outline" className="mt-6" disabled>
                  Visit Ideogram.ai
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Feature Comparison</h2>
            
            <div className="max-w-4xl mx-auto overflow-x-auto">
              <table className="w-full bg-card border rounded-xl">
                <thead>
                  <tr className="border-b">
                    <th className="p-4 text-left">Feature</th>
                    <th className="p-4 text-center">FLUX</th>
                    <th className="p-4 text-center">Ideogram</th>
                    <th className="p-4 text-center">Winner</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-4 font-medium">{row.feature}</td>
                      <td className={`p-4 text-center ${row.winner === 'flux' ? 'text-green-600 font-semibold' : ''}`}>
                        {row.flux}
                      </td>
                      <td className={`p-4 text-center ${row.winner === 'ideogram' ? 'text-blue-600 font-semibold' : ''}`}>
                        {row.ideogram}
                      </td>
                      <td className="p-4 text-center">
                        {row.winner === 'flux' && <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">FLUX</span>}
                        {row.winner === 'ideogram' && <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">Ideogram</span>}
                        {row.winner === 'tie' && <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">Tie</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card border rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Try FLUX?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Experience FLUX's superior bulk generation and photorealistic quality. 5 free images daily, no signup required.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <Link to="/try-generator">Try FLUX Free Now</Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default FluxVsIdeogram;
